import { createClient, RealtimeChannel } from '@supabase/supabase-js';

import { Database } from './types/supabase';

// Import the WaitlistStatus enum from shared package
import { 
  WaitlistStatus, 
  QRCodeType, 
  QRCodeSource 
} from '@wammasys/shared';

// TODO: Move notification services to server-side API routes
// Import the waitlist notification service
// import { sendWaitlistConfirmation, notifyTableReady } from './services/waitlistNotifications';

/*
 * Database Schema Notes:
 * - waitlist_entries: Stores individual customer entries in the waitlist
 * - waitlist_settings: Stores configuration for each restaurant's waitlist (e.g., default_estimated_wait_time)
 * - waitlist_history: Stores historical data for analytics
 * - wait_time_estimates: Stores time estimates based on party size and time of day
 * - waitlist_events: Tracks detailed events for each waitlist entry
 */

// Define WaitlistEntry type
export interface WaitlistEntry {
  id: string;
  restaurant_id: string;
  customer_name: string;
  phone_number: string;
  email?: string | null;
  party_size: number;
  estimated_wait_time?: number | null;
  actual_wait_time?: number | null;
  status: string;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  checked_in_at?: string | null;
  seated_at?: string | null;
  // Add the additional fields from migration 0003
  notification_sent?: boolean;
  notification_sent_at?: string | null;
  customer_notified?: boolean;
  special_requests?: string | null;
  table_assignment?: string | null;
  qr_code_url?: string | null;
  // Fields that might be added in response objects but aren't in the DB
  cancelled_reason?: string | null;
}

// Create Supabase client
const supabaseUrl =
  typeof import.meta !== 'undefined'
    ? import.meta.env.VITE_SUPABASE_URL
    : process.env.VITE_SUPABASE_URL;
const supabaseAnonKey =
  typeof import.meta !== 'undefined'
    ? import.meta.env.VITE_SUPABASE_ANON_KEY
    : process.env.VITE_SUPABASE_ANON_KEY;

// Remote Supabase values (production)
const remoteUrl = 'https://nqfnxhceemuubsxmjfvq.supabase.co';
const remoteKey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5xZm54aGNlZW11dWJzeG1qZnZxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQyNTI1MjksImV4cCI6MjA1OTgyODUyOX0.RgFXwXwA1bUoC9BnUcBODdZkt36KeDzPt5pbnEKrtWs';

// Always use remote connection instead of trying local first
const finalSupabaseUrl = remoteUrl;
const finalSupabaseAnonKey = remoteKey;

export const supabase = createClient<Database>(finalSupabaseUrl, finalSupabaseAnonKey);

// Helper function to calculate estimated wait time based on various factors
export async function calculateWaitTime(
  restaurantId: string,
  partySize: number,
): Promise<{
  estimatedWaitMinutes: number;
  queuePosition: number;
  factors: {
    baseWait: number;
    partySizeModifier: number;
    queueDepthModifier: number;
    timeOfDayModifier: number;
    tableAvailabilityModifier: number;
  };
}> {
  try {
    if (!restaurantId) {
      console.error('Invalid restaurant ID provided to calculateWaitTime');
      throw new Error('Invalid restaurant ID');
    }

    if (typeof partySize !== 'number' || partySize <= 0) {
      console.error('Invalid party size provided to calculateWaitTime:', partySize);
      throw new Error('Invalid party size');
    }

    // 1. Get current queue depth - count of waiting parties
    const { data: waitlistData, error: waitlistError } = await supabase
      .from('waitlist_entries')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .eq('status', 'waiting');

    if (waitlistError) {
      console.error('Error getting waitlist data:', waitlistError);
      // Fallback to a simple estimation if we can't get real data
      return {
        estimatedWaitMinutes: Math.min(partySize * 3, 120),
        queuePosition: 1,
        factors: {
          baseWait: 30,
          partySizeModifier: partySize * 3,
          queueDepthModifier: 0,
          timeOfDayModifier: 0,
          tableAvailabilityModifier: 0,
        },
      };
    }

    // 2. Get restaurant settings from waitlist_settings table
    const { data: settingsData, error: settingsError } = await supabase
      .from('waitlist_settings')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .single();

    if (settingsError) {
      console.error('Error getting waitlist settings:', settingsError);
      // Log more detailed information for debugging
      console.log('Settings query params:', { 
        restaurantId, 
        query: 'waitlist_settings?select=*&restaurant_id=eq.' + restaurantId 
      });
    }

    // Default values if restaurant settings are not available
    const baseWaitTime = settingsData?.default_estimated_wait_time || 15; // minutes
    const waitTimePerPerson = 2; // Default 2 minutes per person
    
    // 3. Calculate queue position (number of parties ahead + 1)
    const queueCount = waitlistData?.length || 0;
    const queuePosition = queueCount + 1;
    
    // 4. Calculate time-based modifier (busy hours: 30% longer wait)
    const currentHour = new Date().getHours();
    let timeOfDayModifier = 1.0;
    
    // Lunch rush (11am-2pm) and dinner rush (5pm-8pm) have longer waits
    if ((currentHour >= 11 && currentHour <= 14) || (currentHour >= 17 && currentHour <= 20)) {
      timeOfDayModifier = 1.3; // 30% increase during rush hours
    }
    
    // Late night has shorter waits
    if (currentHour >= 21 || currentHour <= 5) {
      timeOfDayModifier = 0.7; // 30% decrease during slow hours
    }

    // 5. Consider party size - larger parties need bigger tables
    // Each additional person beyond 2 adds waitTimePerPerson minutes
    const partySizeModifier = Math.max(0, (partySize - 2)) * waitTimePerPerson;
    
    // 6. Calculate impact of queue depth
    // Each party ahead adds a base amount of time
    const queueDepthModifier = (queuePosition - 1) * baseWaitTime * 0.5;
    
    // 7. Table availability factor
    let tableAvailabilityModifier = 0;
    // We don't have table data in our schema, so we'll use a simpler approach
    // Assume tables are limited during peak hours
    if ((currentHour >= 11 && currentHour <= 14) || (currentHour >= 17 && currentHour <= 20)) {
      tableAvailabilityModifier = baseWaitTime * 0.2; // Slight increase during rush hours
    }

    // 8. Calculate the final wait time
    const totalWaitMinutes = Math.max(
      5, // Minimum wait time of 5 minutes
      baseWaitTime + 
      partySizeModifier + 
      queueDepthModifier * timeOfDayModifier +
      tableAvailabilityModifier
    );
    
    // 9. Round to nearest 5 minutes for customer-friendly estimates
    const roundedWaitMinutes = Math.ceil(totalWaitMinutes / 5) * 5;
    
    return {
      estimatedWaitMinutes: roundedWaitMinutes,
      queuePosition,
      factors: {
        baseWait: baseWaitTime,
        partySizeModifier,
        queueDepthModifier,
        timeOfDayModifier,
        tableAvailabilityModifier,
      },
    };
  } catch (error) {
    console.error('Error calculating wait time:', error);
    // Fallback
    return {
      estimatedWaitMinutes: 30,
      queuePosition: 1,
      factors: {
        baseWait: 30,
        partySizeModifier: 0,
        queueDepthModifier: 0,
        timeOfDayModifier: 1,
        tableAvailabilityModifier: 0,
      },
    };
  }
}

// QR Code Functions

/**
 * Generates a unique tracking ID for QR codes
 */
function generateTrackingId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

/**
 * Creates a new QR code for a restaurant
 */
export async function createQRCode(
  restaurantId: string,
  data: {
    name: string;
    type: QRCodeType;
    custom_message?: string;
    design_options?: {
      primaryColor?: string;
      logoPosition?: 'center' | 'top' | 'none';
      size?: 'small' | 'medium' | 'large';
      borderRadius?: number;
      includeRestaurantName?: boolean;
    }
  }
) {
  try {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    // Generate a unique tracking ID
    const trackingId = generateTrackingId();

    // Insert the QR code record
    const { data: qrCode, error } = await supabase
      .from('qr_codes')
      .insert({
        restaurant_id: restaurantId,
        name: data.name,
        type: data.type,
        custom_message: data.custom_message,
        design_options: data.design_options || {},
        tracking_id: trackingId,
        is_active: true
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error creating QR code:', error);
      throw error;
    }

    // Generate URLs for the QR code
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://wammasys.app';
    
    let qrImageUrl = '';
    let downloadUrl = '';

    if (qrCode) {
      // URL that the QR code will point to
      const targetUrl = qrCode.type === QRCodeType.WAITLIST
        ? `${baseUrl}/waitlist/${restaurantId}?source=qr&code=${trackingId}`
        : `${baseUrl}/promotions/${restaurantId}?source=qr&code=${trackingId}`;

      // The QR code data includes the code itself and URLs for accessing it
      return {
        ...qrCode,
        qrUrl: targetUrl,
        qrImageUrl, // This would be generated by a QR code service
        downloadUrl // This would allow download of the QR code image
      };
    }

    throw new Error('Failed to create QR code');
  } catch (error) {
    console.error('Error in createQRCode:', error);
    throw error;
  }
}

/**
 * Gets all QR codes for a restaurant
 */
export async function getQRCodes(
  restaurantId: string,
  filters?: {
    type?: QRCodeType;
    isActive?: boolean;
  }
) {
  try {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    let query = supabase
      .from('qr_codes')
      .select('*')
      .eq('restaurant_id', restaurantId);

    // Apply filters if provided
    if (filters?.type) {
      query = query.eq('type', filters.type);
    }

    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }

    // Order by created_at desc to show newest first
    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error getting QR codes:', error);
      throw error;
    }

    // Generate URLs for each QR code
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://wammasys.app';
    
    return data.map(qrCode => {
      const targetUrl = qrCode.type === QRCodeType.WAITLIST
        ? `${baseUrl}/waitlist/${restaurantId}?source=qr&code=${qrCode.tracking_id}`
        : `${baseUrl}/promotions/${restaurantId}?source=qr&code=${qrCode.tracking_id}`;

      return {
        ...qrCode,
        qrUrl: targetUrl
      };
    });
  } catch (error) {
    console.error('Error in getQRCodes:', error);
    throw error;
  }
}

/**
 * Gets details of a specific QR code
 */
export async function getQRCodeDetails(
  restaurantId: string,
  qrCodeId: string
) {
  try {
    if (!restaurantId || !qrCodeId) {
      throw new Error('Restaurant ID and QR code ID are required');
    }

    const { data: qrCode, error } = await supabase
      .from('qr_codes')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .eq('id', qrCodeId)
      .single();

    if (error) {
      console.error('Error getting QR code details:', error);
      throw error;
    }

    if (!qrCode) {
      throw new Error('QR code not found');
    }

    // Get analytics data
    const { data: scans, error: scansError } = await supabase
      .from('qr_code_scans')
      .select('*')
      .eq('qr_code_id', qrCodeId);

    if (scansError) {
      console.error('Error getting QR code scans:', scansError);
      // Continue without analytics data
    }

    // Generate URL for the QR code
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://wammasys.app';
    
    const targetUrl = qrCode.type === QRCodeType.WAITLIST
      ? `${baseUrl}/waitlist/${restaurantId}?source=qr&code=${qrCode.tracking_id}`
      : `${baseUrl}/promotions/${restaurantId}?source=qr&code=${qrCode.tracking_id}`;

    // Calculate analytics
    const totalScans = scans?.length || 0;
    const uniqueScans = scans ? new Set(scans.map(scan => scan.ip_address)).size : 0;
    const conversions = scans ? scans.filter(scan => scan.converted).length : 0;
    const conversionRate = totalScans > 0 ? (conversions / totalScans) * 100 : 0;

    return {
      ...qrCode,
      qrUrl: targetUrl,
      analytics: {
        totalScans,
        uniqueScans,
        conversions,
        conversionRate: parseFloat(conversionRate.toFixed(2))
      }
    };
  } catch (error) {
    console.error('Error in getQRCodeDetails:', error);
    throw error;
  }
}

/**
 * Updates a QR code
 */
export async function updateQRCode(
  restaurantId: string,
  qrCodeId: string,
  updates: {
    name?: string;
    custom_message?: string;
    design_options?: Record<string, any>;
    is_active?: boolean;
  }
) {
  try {
    if (!restaurantId || !qrCodeId) {
      throw new Error('Restaurant ID and QR code ID are required');
    }

    const { data: qrCode, error } = await supabase
      .from('qr_codes')
      .update(updates)
      .eq('restaurant_id', restaurantId)
      .eq('id', qrCodeId)
      .select('*')
      .single();

    if (error) {
      console.error('Error updating QR code:', error);
      throw error;
    }

    if (!qrCode) {
      throw new Error('QR code not found');
    }

    // Generate URL for the QR code
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://wammasys.app';
    
    const targetUrl = qrCode.type === QRCodeType.WAITLIST
      ? `${baseUrl}/waitlist/${restaurantId}?source=qr&code=${qrCode.tracking_id}`
      : `${baseUrl}/promotions/${restaurantId}?source=qr&code=${qrCode.tracking_id}`;

    return {
      ...qrCode,
      qrUrl: targetUrl
    };
  } catch (error) {
    console.error('Error in updateQRCode:', error);
    throw error;
  }
}

/**
 * Deletes a QR code
 */
export async function deleteQRCode(
  restaurantId: string,
  qrCodeId: string
) {
  try {
    if (!restaurantId || !qrCodeId) {
      throw new Error('Restaurant ID and QR code ID are required');
    }

    const { error } = await supabase
      .from('qr_codes')
      .delete()
      .eq('restaurant_id', restaurantId)
      .eq('id', qrCodeId);

    if (error) {
      console.error('Error deleting QR code:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error in deleteQRCode:', error);
    throw error;
  }
}

/**
 * Tracks a QR code scan
 */
export async function trackQRScan(
  trackingId: string,
  data: {
    userAgent?: string;
    referrer?: string;
    ipAddress?: string;
  }
) {
  try {
    if (!trackingId) {
      throw new Error('Tracking ID is required');
    }

    // First, find the QR code with this tracking ID
    const { data: qrCodes, error: qrCodeError } = await supabase
      .from('qr_codes')
      .select('*')
      .eq('tracking_id', trackingId)
      .limit(1);

    if (qrCodeError || !qrCodes || qrCodes.length === 0) {
      console.error('Error finding QR code with tracking ID:', trackingId);
      throw new Error('QR code not found');
    }

    const qrCode = qrCodes[0];

    // Record the scan
    const { data: scan, error: scanError } = await supabase
      .from('qr_code_scans')
      .insert({
        qr_code_id: qrCode.id,
        tracking_id: trackingId,
        user_agent: data.userAgent,
        referrer: data.referrer,
        ip_address: data.ipAddress,
        converted: false
      })
      .select('*')
      .single();

    if (scanError) {
      console.error('Error recording QR code scan:', scanError);
      throw scanError;
    }

    return { success: true, scanId: scan.id };
  } catch (error) {
    console.error('Error in trackQRScan:', error);
    throw error;
  }
}

/**
 * Updates a QR code scan when a conversion happens
 */
export async function updateQRScanConversion(
  scanId: string,
  conversionId: string
) {
  try {
    if (!scanId || !conversionId) {
      throw new Error('Scan ID and conversion ID are required');
    }

    const { error } = await supabase
      .from('qr_code_scans')
      .update({
        converted: true,
        conversion_id: conversionId
      })
      .eq('id', scanId);

    if (error) {
      console.error('Error updating QR code scan conversion:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateQRScanConversion:', error);
    throw error;
  }
}

/**
 * Gets analytics for QR codes
 */
export async function getQRCodeAnalytics(
  restaurantId: string,
  filters?: {
    qrCodeId?: string;
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }
) {
  try {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    // First, get all QR codes for this restaurant
    const qrCodesQuery = supabase
      .from('qr_codes')
      .select('id')
      .eq('restaurant_id', restaurantId);

    if (filters?.qrCodeId) {
      qrCodesQuery.eq('id', filters.qrCodeId);
    }

    const { data: qrCodes, error: qrCodesError } = await qrCodesQuery;

    if (qrCodesError) {
      console.error('Error getting QR codes for analytics:', qrCodesError);
      throw qrCodesError;
    }

    if (!qrCodes || qrCodes.length === 0) {
      return {
        totalScans: 0,
        uniqueScans: 0,
        conversionRate: 0,
        timeSeriesData: []
      };
    }

    // Get all scans for these QR codes
    const qrCodeIds = qrCodes.map(qr => qr.id);
    
    let scansQuery = supabase
      .from('qr_code_scans')
      .select('*')
      .in('qr_code_id', qrCodeIds);

    if (filters?.startDate) {
      scansQuery = scansQuery.gte('scanned_at', filters.startDate);
    }

    if (filters?.endDate) {
      scansQuery = scansQuery.lte('scanned_at', filters.endDate);
    }

    const { data: scans, error: scansError } = await scansQuery;

    if (scansError) {
      console.error('Error getting QR code scans for analytics:', scansError);
      throw scansError;
    }

    // Calculate analytics
    const totalScans = scans?.length || 0;
    const uniqueScans = scans ? new Set(scans.map(scan => scan.ip_address)).size : 0;
    const conversions = scans ? scans.filter(scan => scan.converted).length : 0;
    const conversionRate = totalScans > 0 ? (conversions / totalScans) * 100 : 0;

    // Group scans by date for time series data
    const timeSeriesData: Array<{ date: string; scans: number; conversions: number }> = [];
    
    if (scans && scans.length > 0) {
      const scansByDate = new Map<string, { scans: number; conversions: number }>();
      
      scans.forEach(scan => {
        const date = new Date(scan.scanned_at).toISOString().split('T')[0]; // YYYY-MM-DD
        const existing = scansByDate.get(date) || { scans: 0, conversions: 0 };
        
        existing.scans += 1;
        if (scan.converted) {
          existing.conversions += 1;
        }
        
        scansByDate.set(date, existing);
      });
      
      // Convert Map to array and sort by date
      scansByDate.forEach((value, key) => {
        timeSeriesData.push({
          date: key,
          scans: value.scans,
          conversions: value.conversions
        });
      });
      
      timeSeriesData.sort((a, b) => a.date.localeCompare(b.date));
    }

    return {
      totalScans,
      uniqueScans,
      conversionRate: parseFloat(conversionRate.toFixed(2)),
      timeSeriesData
    };
  } catch (error) {
    console.error('Error in getQRCodeAnalytics:', error);
    throw error;
  }
}

/**
 * Adds a promotional subscriber from QR code form
 */
export async function addPromotionSubscriber(
  restaurantId: string,
  data: {
    name: string;
    email?: string;
    phone?: string;
    consent: boolean;
    source?: string;
    source_id?: string;
  }
) {
  try {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    if (!data.name) {
      throw new Error('Name is required');
    }

    if (!data.email && !data.phone) {
      throw new Error('Either email or phone is required');
    }

    const { data: subscriber, error } = await supabase
      .from('promotion_subscribers')
      .insert({
        restaurant_id: restaurantId,
        name: data.name,
        email: data.email,
        phone: data.phone,
        consent: data.consent,
        source: data.source || 'qr_code',
        source_id: data.source_id
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error adding promotion subscriber:', error);
      throw error;
    }

    // If we have a source_id and it's a QR code scan, update the conversion
    if (data.source === 'qr_code' && data.source_id) {
      try {
        const { data: scans } = await supabase
          .from('qr_code_scans')
          .select('id')
          .eq('tracking_id', data.source_id)
          .order('scanned_at', { ascending: false })
          .limit(1);

        if (scans && scans.length > 0) {
          await updateQRScanConversion(scans[0].id, subscriber.id);
        }
      } catch (conversionError) {
        console.error('Error updating QR scan conversion:', conversionError);
        // Continue even if conversion tracking fails
      }
    }

    return { success: true, subscriberId: subscriber.id };
  } catch (error) {
    console.error('Error in addPromotionSubscriber:', error);
    throw error;
  }
}

/**
 * Gets promotional subscribers for a restaurant
 */
export async function getPromotionSubscribers(
  restaurantId: string,
  filters?: {
    source?: string;
    sourceId?: string;
    startDate?: string;
    endDate?: string;
  }
) {
  try {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    let query = supabase
      .from('promotion_subscribers')
      .select('*')
      .eq('restaurant_id', restaurantId);

    if (filters?.source) {
      query = query.eq('source', filters.source);
    }

    if (filters?.sourceId) {
      query = query.eq('source_id', filters.sourceId);
    }

    if (filters?.startDate) {
      query = query.gte('created_at', filters.startDate);
    }

    if (filters?.endDate) {
      query = query.lte('created_at', filters.endDate);
    }

    // Order by created_at desc to show newest first
    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error getting promotion subscribers:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getPromotionSubscribers:', error);
    throw error;
  }
}

// Update addToWaitlist to track QR code source if provided
export async function addToWaitlist(
  restaurantId: string,
  waitlistEntry: Omit<
    WaitlistEntry,
    'id' | 'created_at' | 'restaurant_id' | 'status' | 'estimated_wait_time'
  > & {
    source?: QRCodeSource;
    source_id?: string;
  },
) {
  try {
    // Validate input data
    if (!restaurantId) {
      console.error('Missing required restaurantId parameter');
      return { error: { message: 'Restaurant ID is required' } };
    }
    
    if (!waitlistEntry.customer_name) {
      console.error('Missing required customer_name parameter');
      return { error: { message: 'Customer name is required' } };
    }
    
    if (!waitlistEntry.phone_number) {
      console.error('Missing required phone_number parameter');
      return { error: { message: 'Phone number is required' } };
    }
    
    if (!waitlistEntry.party_size || typeof waitlistEntry.party_size !== 'number' || waitlistEntry.party_size <= 0) {
      console.error('Invalid party_size parameter:', waitlistEntry.party_size);
      return { error: { message: 'Valid party size is required' } };
    }

    // Calculate estimated wait time
    const { estimatedWaitMinutes } = await calculateWaitTime(
      restaurantId, 
      waitlistEntry.party_size
    );

    // Create the waitlist entry with added source tracking
    const { data, error } = await supabase
        .from('waitlist_entries')
      .insert({
        restaurant_id: restaurantId,
        customer_name: waitlistEntry.customer_name,
        phone_number: waitlistEntry.phone_number,
        email: waitlistEntry.email || null,
        party_size: waitlistEntry.party_size,
        notes: waitlistEntry.notes || null,
        status: WaitlistStatus.WAITING,
        estimated_wait_time: estimatedWaitMinutes,
        source: waitlistEntry.source || QRCodeSource.DIRECT,
        source_id: waitlistEntry.source_id,
        customer_notified: waitlistEntry.customer_notified
      })
      .select('*');

    if (error) {
      console.error('Error adding to waitlist:', error);
      throw new Error(`Failed to add to waitlist: ${error.message}`);
    }

    if (!data || data.length === 0) {
      throw new Error('No data returned from waitlist entry creation');
    }

    // If this was from a QR code scan, update the conversion
    if (waitlistEntry.source === QRCodeSource.QR_CODE && waitlistEntry.source_id) {
      try {
        const { data: scans } = await supabase
          .from('qr_code_scans')
          .select('id')
          .eq('tracking_id', waitlistEntry.source_id)
          .order('scanned_at', { ascending: false })
          .limit(1);

        if (scans && scans.length > 0) {
          await updateQRScanConversion(scans[0].id, data[0].id);
        }
      } catch (conversionError) {
        console.error('Error updating QR scan conversion:', conversionError);
        // Continue even if conversion tracking fails
      }
    }

    // Recalculate queue positions for all entries
    await recalculateQueuePositions(restaurantId);

    // Send confirmation SMS if the customer opted in
    if (data && data.length > 0 && waitlistEntry.customer_notified) {
      // TODO: Move notification functionality to server-side API routes
      console.log('SMS notification skipped - notification service temporarily disabled');
      
      /* Original notification code - to be moved to server-side:
      try {
        const smsResult = await sendWaitlistConfirmation(data[0].id);
        
        // Log the SMS result but don't let it affect the waitlist entry creation
        if (!smsResult.success) {
          console.error('SMS notification failed but waitlist entry was created:', smsResult.error);
        }
      } catch (smsError) {
        console.error('Error sending confirmation SMS:', smsError);
        // Don't throw here, we still want to return the waitlist entry
      }
      */
    }

    return { data: data[0], error: null };
  } catch (error) {
    console.error('Error in addToWaitlist:', error);
    return { 
      data: null, 
      error: { 
        message: error instanceof Error ? error.message : 'Failed to add to waitlist'
      } 
    };
  }
}

// Add a new function to recalculate queue positions for all waiting entries
async function recalculateQueuePositions(restaurantId: string) {
  // This function is still called but we'll just make it a no-op since queue_position doesn't exist
  console.log('Queue position recalculation skipped - column does not exist in the schema');
  return;
}

// Function to get a single waitlist entry
export const getWaitlistEntry = async (entryId: string) => {
  return supabase
    .from('waitlist_entries')
    .select('*')
    .eq('id', entryId)
    .single();
};

// Rename function to match API naming conventions (getWaitlistEntries -> viewWaitlist)
export const viewWaitlist = async (
  restaurantId: string, 
  filters?: { 
    status?: WaitlistStatus[],
    limit?: number,
    offset?: number
  }
) => {
  try {
    let query = supabase
    .from('waitlist_entries')
    .select('*')
      .eq('restaurant_id', restaurantId);
    
    // Apply filters if provided
    if (filters?.status && filters.status.length > 0) {
      query = query.in('status', filters.status);
    }
    
    // Apply pagination if provided
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }
    
    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
    }
    
    // Always sort by created_at for consistency
    query = query.order('created_at', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching waitlist:', error);
      return { error };
    }
    
    return { data };
  } catch (error) {
    console.error('Error in viewWaitlist:', error);
    return { error: { message: 'Failed to retrieve waitlist' } };
  }
};

// Keep the old function name as an alias for backward compatibility
export const getWaitlistEntries = viewWaitlist;

// Implement real-time subscription for waitlist updates
export function subscribeToWaitlist(
  restaurantId: string,
  callback: (waitlistEntries: WaitlistEntry[]) => void,
  filters?: { status?: WaitlistStatus[] }
): () => void {
  // Create a unique channel name based on restaurant ID
  const channelName = `waitlist-updates-${restaurantId}`;
  
  // Create a channel and subscribe to changes
  const channel: RealtimeChannel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
        schema: 'public',
        table: 'waitlist_entries',
        filter: `restaurant_id=eq.${restaurantId}`
      },
      async (payload) => {
        console.log('Received real-time update:', payload);
        
        // After any change, fetch the latest waitlist data
        try {
          const { data, error } = await viewWaitlist(restaurantId, filters);
          
          if (error) {
            console.error('Error fetching updated waitlist:', error);
            return;
          }
          
          if (data) {
            // Call the callback with the latest data
            callback(data);
          }
        } catch (error) {
          console.error('Error handling waitlist update:', error);
        }
      }
    )
    .subscribe((status) => {
      console.log(`Waitlist subscription status for ${restaurantId}:`, status);
    });
  
  // Return a function to unsubscribe
  return () => {
    console.log(`Unsubscribing from waitlist updates for ${restaurantId}`);
    supabase.channel(channelName).unsubscribe();
  };
}

// Update the updateWaitlistEntry function
export const updateWaitlistEntry = async (id: string, data: Partial<WaitlistEntry>) => {
  try {
    // Store the previous entry to check if status has changed
    const { data: previousEntry } = await supabase
      .from('waitlist_entries')
      .select('status, restaurant_id')
      .eq('id', id)
      .single();

    // Update the entry
    const result = await supabase
      .from('waitlist_entries')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    // If status changed and we have the restaurant ID, recalculate positions
    if (
      previousEntry && 
      data.status && 
      previousEntry.status !== data.status && 
      previousEntry.restaurant_id && 
      (previousEntry.status === WaitlistStatus.WAITING || data.status === WaitlistStatus.WAITING)
    ) {
      await recalculateQueuePositions(previousEntry.restaurant_id);
      
      // Log the status change
      await logWaitlistEvent({
        waitlist_entry_id: id,
        event_type: 'status_change',
        previous_status: previousEntry.status,
        new_status: data.status,
        timestamp: new Date().toISOString()
      });
    }

    return result;
  } catch (error) {
    console.error('Error updating waitlist entry:', error);
    return { data: null, error };
  }
};

// Update seatCustomer function
export const seatCustomer = async (entryId: string): Promise<{ data: any; error: any }> => {
  try {
    // First, try to notify the customer their table is ready
    // TODO: Move notification functionality to server-side API routes
    console.log('Table ready notification skipped - notification service temporarily disabled');
    
    /* Original notification code - to be moved to server-side:
    try {
      await notifyTableReady(entryId);
    } catch (notifyError) {
      console.error('Error notifying customer:', notifyError);
      // Continue even if notification fails
    }
    */

    // Then update their status to seated
    const { data, error } = await supabase
      .from('waitlist_entries')
      .update({
        status: WaitlistStatus.SEATED,
        seated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', entryId)
      .select();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error seating customer:', error);
    return { data: null, error };
  }
};

// Update cancelWaitlistEntry function
export async function cancelWaitlistEntry(
  restaurantId: string,
  waitlistEntryId: string,
  reason?: string
) {
  try {
    const now = new Date().toISOString();
    
    // Get the current status first for event logging
    const { data: entry } = await supabase
      .from('waitlist_entries')
      .select('status')
      .eq('id', waitlistEntryId)
      .eq('restaurant_id', restaurantId) // Add restaurant_id check for security
      .single();
      
    // Update the entry status
    const result = await supabase
      .from('waitlist_entries')
      .update({
        status: WaitlistStatus.CANCELLED,
        cancelled_reason: reason,
        updated_at: now
      })
      .eq('id', waitlistEntryId)
      .eq('restaurant_id', restaurantId) // Add restaurant_id check for security
      .select()
      .single();
    
    if (result.error) {
      console.error('Error cancelling waitlist entry:', result.error);
      return { error: result.error };
    }
    
    // Recalculate queue positions if the entry was previously waiting
    if (entry && entry.status === WaitlistStatus.WAITING) {
      await recalculateQueuePositions(restaurantId);
      
      // Log the cancellation event
      await logWaitlistEvent({
        waitlist_entry_id: waitlistEntryId,
        event_type: 'status_change',
        previous_status: entry.status,
        new_status: WaitlistStatus.CANCELLED,
        cancellation_reason: reason,
        timestamp: now
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error cancelling waitlist entry:', error);
    return { data: null, error };
  }
}

// Implement the logWaitlistEvent function to track waitlist status changes
export const logWaitlistEvent = async (eventData: {
  waitlist_entry_id: string;
  event_type: string;
  previous_status?: string;
  new_status?: string;
  cancellation_reason?: string;
  timestamp: string;
  metadata?: Record<string, any>;
}) => {
  try {
    const { data, error } = await supabase
      .from('waitlist_events')
      .insert([eventData])
      .select();

    if (error) {
      console.error('Error logging waitlist event:', error);
      return { error };
    }

    return { data };
  } catch (error) {
    console.error('Error in logWaitlistEvent:', error);
    return { error: { message: 'Failed to log waitlist event' } };
  }
};

// Function to get waitlist history
export const getWaitlistHistory = async (restaurantId: string, startDate: string, endDate: string) => {
  try {
    const { data, error } = await supabase
      .from('waitlist_history')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });
    
    if (error) {
      console.error('Error getting waitlist history:', error);
      return { error };
    }
    
    return { data };
  } catch (error) {
    console.error('Error in getWaitlistHistory:', error);
    return { error: { message: 'Failed to get waitlist history' } };
  }
};

// Function to get waitlist settings
export const getWaitlistSettings = async (restaurantId: string) => {
  try {
    const { data, error } = await supabase
      .from('waitlist_settings')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .single();
    
    if (error) {
      console.error('Error getting waitlist settings:', error);
      return { error };
    }
    
    return { data };
  } catch (error) {
    console.error('Error in getWaitlistSettings:', error);
    return { error: { message: 'Failed to get waitlist settings' } };
  }
};

// Function to update waitlist settings
export const updateWaitlistSettings = async (restaurantId: string, data: any) => {
  try {
    const { data: updatedData, error } = await supabase
      .from('waitlist_settings')
      .update(data)
      .eq('restaurant_id', restaurantId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating waitlist settings:', error);
      return { error };
    }
    
    return { data: updatedData };
  } catch (error) {
    console.error('Error in updateWaitlistSettings:', error);
    return { error: { message: 'Failed to update waitlist settings' } };
  }
};
